import { useQuery } from "@tanstack/react-query";
import FeedbackWidget from "@/components/dash/FeedbackWidget";
import { dashApi, type SourceMeta } from "@/lib/dashApi";
import { fmtRelative } from "@/lib/format";
import {
  BarList,
  EmptyState,
  ErrorState,
  GapList,
  KpiCard,
  LoadingState,
  PageHeader,
  Panel,
  StatusPill,
} from "@/components/dash/ui";

function SourceRight({ meta }: { meta?: SourceMeta }) {
  if (!meta?.available) return null;
  return (
    <span className="flex items-center gap-2">
      <StatusPill status={meta.status ?? "unknown"} />
      <span className="text-xs text-slate-500">{fmtRelative(meta.captured_at)}</span>
    </span>
  );
}

export default function RenewalsBillingPage() {
  const q = useQuery({ queryKey: ["payments-renewals"], queryFn: dashApi.paymentsRenewals });
  const d = q.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Renewals & Billing Risk"
        description="IronPayments operational collection state. Expected values are plan-price derivations — never settled revenue. Accounting truth remains Odoo/Holded."
        right={<SourceRight meta={d?.source} />}
      />
      {q.isLoading ? (
        <LoadingState />
      ) : q.isError ? (
        <ErrorState error={q.error} />
      ) : !d || !d.source.available ? (
        <EmptyState>No IronPayments snapshot has been captured yet.</EmptyState>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
            <KpiCard label="Active subscriptions" value={d.headline.active_subscriptions} unit="count" />
            <KpiCard label="MRR" value={d.headline.mrr} unit="EUR" />
            <KpiCard label="Renewals due 30d" value={d.renewals?.upcoming_30d?.count ?? null} unit="count" />
            <KpiCard label="Overdue renewal cycles" value={d.renewals?.overdue_cycles ?? null} unit="count" tone={d.renewals?.overdue_cycles ? "danger" : undefined} />
            <KpiCard label="Provisioning pending" value={d.provisioning?.pending ?? null} unit="count" tone={d.provisioning?.pending ? "danger" : undefined} />
            <KpiCard label="Failed payments 24h" value={d.headline.failed_payments_24h} unit="count" tone={d.headline.failed_payments_24h ? "danger" : undefined} />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Panel
              title="Upcoming renewal exposure"
              subtitle={d.renewals?.prices_available === false ? "Plan prices unavailable in the source — counts shown, expected € withheld (not shown as €0)." : "Expected € is plan-price derived — not settled revenue."}
            >
              {d.renewals ? (
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-slate-400">Due next 7 days</dt><dd className="font-medium text-slate-200">{d.renewals.upcoming_7d.count} sub(s){d.renewals.upcoming_7d.expected_total !== null ? ` · €${d.renewals.upcoming_7d.expected_total.toLocaleString()}` : " · € —"}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-400">Due next 30 days</dt><dd className="font-medium text-slate-200">{d.renewals.upcoming_30d.count} sub(s){d.renewals.upcoming_30d.expected_total !== null ? ` · €${d.renewals.upcoming_30d.expected_total.toLocaleString()}` : " · € —"}</dd></div>
                </dl>
              ) : (
                <EmptyState compact>Not available in this snapshot.</EmptyState>
              )}
            </Panel>
            <Panel title="Renewal pipeline (12.4A workflow)" subtitle={d.renewals?.workflow_in_use ? undefined : "Workflow not yet in use — states populate when staff adopt it."}>
              <BarList rows={d.renewals?.cycles_by_state ?? []} unit="count" empty="No renewal cycles recorded yet — the renewal workflow has not been used." />
              {d.renewals && d.renewals.attempts_by_status.length > 0 && <div className="mt-3"><BarList rows={d.renewals.attempts_by_status} unit="count" /></div>}
            </Panel>
            <Panel title="Billing-method mix (active subscriptions)" subtitle={`${d.billing_methods?.open_change_requests ?? 0} open method-change request(s)`}>
              <BarList rows={d.billing_methods?.mix ?? []} unit="count" empty="No active subscriptions." />
            </Panel>
            <Panel title="Provisioning state" subtitle={`${d.provisioning?.service_expiring_7d ?? 0} service credential(s) expiring within 7 days`}>
              <BarList rows={d.provisioning?.by_state ?? []} unit="count" empty="No provisioning data." />
            </Panel>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <Panel title="Forward exposure vs settled" subtitle="Left: plan-price value still DUE (not yet renewed) in the next 30 days — a forward estimate, not settled and not a like-for-like of the right column. Right: money actually collected in the last 30 days.">
              {d.expected_vs_settled ? (
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-slate-400">Still due, unrenewed (30d)</dt><dd className="font-medium text-slate-200">{d.expected_vs_settled.expected !== null ? `€${d.expected_vs_settled.expected.toLocaleString()}` : "— (plan prices unavailable)"}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-400">Settled — payments ledger</dt><dd className="font-medium text-slate-200">€{d.expected_vs_settled.settled_payments_ledger.toLocaleString()}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-400">Settled — verified manual</dt><dd className="font-medium text-slate-200">€{d.expected_vs_settled.settled_manual.toLocaleString()} ({d.expected_vs_settled.settled_manual_count})</dd></div>
                  <div className="flex justify-between border-t border-slate-700/60 pt-2"><dt className="text-slate-300">Settled total (30d)</dt><dd className="font-semibold text-slate-100">€{d.expected_vs_settled.settled_total.toLocaleString()}</dd></div>
                </dl>
              ) : (
                <EmptyState compact>Not available in this snapshot.</EmptyState>
              )}
            </Panel>
            <Panel title="Dunning" subtitle="Stripe-billed subscriptions only (webhook-driven).">
              {d.dunning ? (
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-slate-400">Active cases</dt><dd className="font-medium text-slate-200">{d.dunning.active}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-400">Oldest case</dt><dd className="font-medium text-slate-200">{d.dunning.oldest_days} day(s)</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-400">Pending cancellation</dt><dd className="font-medium text-slate-200">{d.headline.pending_cancel ?? "—"}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-400">Cancelled in range</dt><dd className="font-medium text-slate-200">{d.headline.cancelled_in_range ?? "—"} ({d.cancel_reasons_recorded ?? 0} with a recorded reason)</dd></div>
                </dl>
              ) : (
                <EmptyState compact>Not available in this snapshot.</EmptyState>
              )}
            </Panel>
            <Panel title="Reminder delivery health" subtitle="Outbound payment/renewal communications — failures silently break collections.">
              {d.comm_health ? (
                <>
                  {d.comm_health.failure_rate_pct !== null && d.comm_health.failure_rate_pct >= 50 && d.comm_health.total >= 10 && (
                    <p className="mb-2 rounded bg-rose-500/10 px-2 py-1 text-xs text-rose-300">
                      {d.comm_health.failure_rate_pct}% of {d.comm_health.total} messages failed — customers may not be receiving reminders.
                    </p>
                  )}
                  <BarList rows={d.comm_health.by_state} unit="count" empty="No outbound communications in range." />
                </>
              ) : (
                <EmptyState compact>Not available in this snapshot.</EmptyState>
              )}
            </Panel>
          </div>

          <GapList gaps={d.source.gaps} />
          <FeedbackWidget targetType="page" targetRef="/payments/renewals" />
        </>
      )}
    </div>
  );
}
